import React, { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { GlobalSwitch } from '../../../components/GlobalComponents/GlobalSwitch';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';

interface PermissionSettings {
  enabled: boolean;
  personal: { enabled: boolean };
  parents: { enabled: boolean };
  contactDetails: { enabled: boolean };
}

interface Settings {
  editProfile: PermissionSettings;
  viewProfile: PermissionSettings;
}

const OPTION_EDIT_PROFILE = 'form_teacher_edit_profile';
const OPTION_VIEW_PROFILE = 'form_teacher_view_profile';

const defaultPermissionSettings = (): PermissionSettings => ({
  enabled: false,
  personal: { enabled: false },
  parents: { enabled: false },
  contactDetails: { enabled: false },
});

const defaultSettings = (): Settings => ({
  editProfile: defaultPermissionSettings(),
  viewProfile: defaultPermissionSettings(),
});

const FormTeacherSettings: React.FC = () => {
  const toast = useRef<Toast>(null);
  const [settings, setSettings] = useState<Settings>(defaultSettings());
  const [saving, setSaving] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  // GET /api/user-options/:name returns
  // { status, data: { option_name, option_value, autoload } }
  // A 404 just means the option hasn't been saved yet — fall back to defaults
  // instead of surfacing an error.
  const fetchOption = async (name: string): Promise<PermissionSettings> => {
    try {
      const res = await apiClient.get(`/api/user-options/${name}`);
      if (res.data?.status === 'success' && res.data?.data?.option_value) {
        return res.data.data.option_value as PermissionSettings;
      }
      return defaultPermissionSettings();
    } catch (err: any) {
      if (err?.response?.status === 404) {
        return defaultPermissionSettings();
      }
      throw err;
    }
  };

  const fetchSettings = async (): Promise<void> => {
    setLoading(true);
    try {
      const [editProfile, viewProfile] = await Promise.all([
        fetchOption(OPTION_EDIT_PROFILE),
        fetchOption(OPTION_VIEW_PROFILE),
      ]);

      setSettings({ editProfile, viewProfile });
    } catch (err: any) {
      console.error('Failed to fetch settings:', err);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Failed to fetch settings',
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSetting = (
    section: keyof Settings,
    key: 'enabled' | 'personal' | 'parents' | 'contactDetails'
  ) => {
    setSettings((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]:
          key === 'enabled'
            ? !prev[section][key]
            : { ...prev[section][key], enabled: !prev[section][key].enabled },
      },
    }));
  };

  const handleUpdateSettings = async (): Promise<void> => {
    const isEditProfileEnabled = settings.editProfile.enabled;
    const isViewProfileEnabled = settings.viewProfile.enabled;

    if (isEditProfileEnabled) {
      const hasEditSubSetting =
        settings.editProfile.personal.enabled ||
        settings.editProfile.parents.enabled ||
        settings.editProfile.contactDetails.enabled;

      if (!hasEditSubSetting) {
        toast.current?.show({
          severity: 'warn',
          summary: 'Validation Error',
          detail: 'At least one Edit Profile sub-setting (Personal, Parents, or Contact Details) must be enabled.',
          life: 5000,
        });
        return;
      }
    }

    if (isViewProfileEnabled) {
      const hasViewSubSetting =
        settings.viewProfile.personal.enabled ||
        settings.viewProfile.parents.enabled ||
        settings.viewProfile.contactDetails.enabled;

      if (!hasViewSubSetting) {
        toast.current?.show({
          severity: 'warn',
          summary: 'Validation Error',
          detail: 'At least one View Profile sub-setting (Personal, Parents, or Contact Details) must be enabled.',
          life: 5000,
        });
        return;
      }
    }

    setSaving(true);
    try {
      // POST /api/user-options/multiple-batch expects a flat
      // { option_name: value } map and returns
      // { status: "success" | "partial_error", results: { [name]: { status, message? } } }
      const res = await apiClient.post('/api/user-options/multiple-batch', {
        [OPTION_EDIT_PROFILE]: settings.editProfile,
        [OPTION_VIEW_PROFILE]: settings.viewProfile,
      });

      if (res.data?.status === 'success') {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Settings updated successfully',
          life: 4000,
        });
      } else {
        const failed = Object.entries(res.data?.results || {})
          .filter(([, r]: [string, any]) => r?.status === 'error')
          .map(([name, r]: [string, any]) => `${name}: ${r.message}`)
          .join('; ');
        throw new Error(failed || 'Failed to update settings');
      }
    } catch (err: any) {
      console.error('Failed to update settings:', err);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || err.message || 'Failed to update settings',
        life: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  const renderPermissionCard = (
    section: keyof Settings,
    title: string,
    description: string,
    icon: string,
    gradient: string
  ) => {
    const sectionSettings = settings[section];

    return (
      <div
        style={{
          background: '#f8fafc',
          borderRadius: '12px',
          padding: '20px',
          border: '1px solid #e5e7eb',
        }}
      >
        {/* Main Toggle */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: sectionSettings.enabled ? '16px' : '0',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '63px',
                height: '36px',
                borderRadius: '8px',
                background: gradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <i className={icon} style={{ fontSize: '16px', color: 'white' }}></i>
            </div>
            <div>
              <div
                style={{
                  fontSize: '15px',
                  fontWeight: '600',
                  color: '#1f2937',
                  marginBottom: '2px',
                }}
              >
                {title}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginRight: '5px' }}>{description}</div>
            </div>
          </div>
          <GlobalSwitch
            value={sectionSettings.enabled}
            onChange={() => handleToggleSetting(section, 'enabled')}
          />
        </div>

        {/* Sub-Settings */}
        {sectionSettings.enabled && (
          <div style={{ marginLeft: '48px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Personal */}
            <div
              style={{
                background: 'white',
                borderRadius: '8px',
                padding: '12px',
                border: '1px solid #e5e7eb',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: sectionSettings.personal.enabled ? '8px' : '0',
                }}
              >
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                  Personal Information
                </span>
                <GlobalSwitch
                  value={sectionSettings.personal.enabled}
                  onChange={() => handleToggleSetting(section, 'personal')}
                />
              </div>
              {sectionSettings.personal.enabled && (
                <div
                  style={{
                    padding: '10px 12px',
                    background: '#f0f9ff',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: '#1e40af',
                    lineHeight: 1.5,
                    border: '1px solid #bfdbfe',
                  }}
                >
                  Includes: Admission Number, Date of Birth, Gender, Blood Group, Address, and Optional Subject
                </div>
              )}
            </div>

            {/* Parents */}
            <div
              style={{
                background: 'white',
                borderRadius: '8px',
                padding: '12px',
                border: '1px solid #e5e7eb',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: sectionSettings.parents.enabled ? '8px' : '0',
                }}
              >
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                  Parent Information
                </span>
                <GlobalSwitch
                  value={sectionSettings.parents.enabled}
                  onChange={() => handleToggleSetting(section, 'parents')}
                />
              </div>
              {sectionSettings.parents.enabled && (
                <div
                  style={{
                    padding: '10px 12px',
                    background: '#f0f9ff',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: '#1e40af',
                    lineHeight: 1.5,
                    border: '1px solid #bfdbfe',
                  }}
                >
                  Includes: Parent names and contact information
                </div>
              )}
            </div>

            {/* Contact Details */}
            <div
              style={{
                background: 'white',
                borderRadius: '8px',
                padding: '12px',
                border: '1px solid #e5e7eb',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: sectionSettings.contactDetails.enabled ? '8px' : '0',
                }}
              >
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                  Contact Details
                </span>
                <GlobalSwitch
                  value={sectionSettings.contactDetails.enabled}
                  onChange={() => handleToggleSetting(section, 'contactDetails')}
                />
              </div>
              {sectionSettings.contactDetails.enabled && (
                <div
                  style={{
                    padding: '10px 12px',
                    background: '#f0f9ff',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: '#1e40af',
                    lineHeight: 1.5,
                    border: '1px solid #bfdbfe',
                  }}
                >
                  Includes: Contact Number and Email ID
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div
        style={{
          background: 'white',
          borderRadius: '16px',
          padding: '60px 24px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(30, 64, 175, 0.08)',
          border: '1px solid rgba(59, 130, 246, 0.12)',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            width: '40px',
            height: '40px',
            border: '4px solid #e5e7eb',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px',
          }}
        ></div>
        <p style={{ color: '#64748b', fontSize: '14px' }}>Loading form teacher settings...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <div
        style={{
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(30, 64, 175, 0.08)',
          border: '1px solid rgba(59, 130, 246, 0.12)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            paddingBottom: '16px',
            borderBottom: '2px solid #f3f4f6',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <i className="pi pi-user" style={{ fontSize: '18px', color: 'white' }}></i>
            </div>
            <div>
              <h2
                style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#1e40af',
                  margin: 0,
                }}
              >
                Form Teacher Settings
              </h2>
              <p
                style={{
                  fontSize: '13px',
                  color: '#64748b',
                  margin: '2px 0 0 0',
                }}
              >
                Configure permissions for form teachers to view and edit student profiles
              </p>
            </div>
          </div>

          <GlobalButton
            label={saving ? 'Updating...' : 'Update Settings'}
            onClick={handleUpdateSettings}
            disabled={saving}
            loading={saving}
            icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
            variant="success"
            size="small"
          />
        </div>

        {/* Permission Cards */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '20px',
          }}
        >
          {renderPermissionCard(
            'editProfile',
            'Edit Profile',
            'Allow form teachers to edit student information',
            'pi pi-pencil',
            'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)'
          )}

          {renderPermissionCard(
            'viewProfile',
            'View Profile',
            'Allow form teachers to view student information',
            'pi pi-eye',
            'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)'
          )}
        </div>
      </div>
    </>
  );
};

export default FormTeacherSettings;